import { ToastService } from "@shared/lib/services";
import { ExternalPayoutStatus, IPayoutInfluencer, PAYOUTSTATUS } from "@shared/lib/types/briefCampains";
import { getBankVerificationLink } from "@shared/lib/utils/getBankVerificationLink";
import { Button, Popconfirm } from "@shared/ui";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import { Dropdown, MenuProps } from "antd";
import { ColumnsType } from "antd/es/table";
import { Check, Copy, MoreHorizontal, Plus, X } from "lucide-react";
import { useMemo, useState } from "react";
import { canCancelPayout, formatPayoutAmount, formatPayoutDate } from "../lib/payoutDisplay";
import { PayoutCreatorCell } from "../ui/PayoutCreatorCell";
import { PayoutStatusCell } from "../ui/PayoutStatusCell";
import { PayoutVendorBillingCell } from "../ui/PayoutVendorBillingCell";
import { getPayoutCampaignColumn } from "./getPayoutCampaignColumn";
import styles from "./usePayoutsColumns.module.scss";

type BriefOption = {
  label: string;
  value: string;
};

type UsePayoutsColumnsParams = {
  isCancelling: boolean;
  onCancelPayout: (vendorPaymentId: string) => void;
  onViewPayments: (campaignInfluencerId: string) => void;
  onAddBillingDetails?: (record: IPayoutInfluencer) => void;
  briefOptions: BriefOption[];
  selectedBriefIds: string[];
  onBriefIdsChange: (ids: string[]) => void;
  briefSearch: string;
  onBriefSearchChange: (value: string) => void;
  areBriefsLoading: boolean;
};

type PayoutActionsCellProps = {
  record: IPayoutInfluencer;
  isCancelling: boolean;
  onCancelPayout: (vendorPaymentId: string) => void;
  onViewPayments: (campaignInfluencerId: string) => void;
  onAddBillingDetails?: (record: IPayoutInfluencer) => void;
};

function PayoutActionsCell({
  record,
  isCancelling,
  onCancelPayout,
  onViewPayments,
  onAddBillingDetails,
}: PayoutActionsCellProps) {
  const [copied, setCopied] = useState(false);

  const verificationLink = getBankVerificationLink(record);
  const isPendingPayout =
    record.externalPayoutStatus === ExternalPayoutStatus.Pending &&
    record.payoutStatus === PAYOUTSTATUS.Pending;
  const isCancelable = canCancelPayout(record);

  const handleCopy = async () => {
    if (!verificationLink) return;
    try {
      await navigator.clipboard.writeText(verificationLink);
      setCopied(true);
      ToastService.success("Link copied to clipboard");
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      ToastService.error("Failed to copy link");
    }
  };

  const menuItems: MenuProps["items"] = [
    {
      key: "view-payments",
      label: "View Payments",
      onClick: () => onViewPayments(record.campaignInfluencerId),
    },
  ];

  return (
    <div className={styles.actionCell}>
      {/* 1. Copy icon */}
      {verificationLink ? (
        <Button
          type="text"
          size="small"
          icon={copied ? <Check size={14} /> : <Copy size={14} />}
          onClick={handleCopy}
          title="Copy link"
          style={{ width: 32 }}
        />
      ) : null}

      {/* 2. Cancel icon (Cross) */}
      {isCancelable ? (
        <Popconfirm
          title="Cancel this payout?"
          okText="Cancel payout"
          okButtonProps={{ danger: true, loading: isCancelling }}
          onConfirm={() => onCancelPayout(record.vendorPaymentId || record._id)}
        >
          <Button
            type="text"
            size="small"
            icon={<X size={14} />}
            title="Cancel link"
            danger
            style={{ width: 32 }}
          />
        </Popconfirm>
      ) : null}

      {/* 3. Add (Plus) icon */}
      {isPendingPayout && onAddBillingDetails ? (
        <Button
          type="text"
          size="small"
          icon={<Plus size={14} />}
          onClick={() => onAddBillingDetails(record)}
          title="Add billing details"
          style={{ width: 32 }}
        />
      ) : null}

      {/* 4. More icon */}
      <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
        <Button
          type="text"
          size="small"
          icon={<MoreHorizontal size={16} />}
          title="More actions"
          style={{ width: 32 }}
        />
      </Dropdown>
    </div>
  );
}

export function usePayoutsColumns({
  isCancelling,
  onCancelPayout,
  onViewPayments,
  onAddBillingDetails,
  briefOptions,
  selectedBriefIds,
  onBriefIdsChange,
  briefSearch,
  onBriefSearchChange,
  areBriefsLoading,
}: UsePayoutsColumnsParams): ColumnsType<IPayoutInfluencer> {
  return useMemo(
    () => [
      {
        title: "Creator",
        key: "creator",
        width: 220,
        render: (_value: unknown, record: IPayoutInfluencer) => (
          <PayoutCreatorCell
            name={record.campaignInfluencer?.influencerName}
            handle={record.campaignInfluencer?.socialMediaName}
            profileUrl={record.campaignInfluencer?.socialMediaLink}
          />
        ),
      },
      getPayoutCampaignColumn({
        briefOptions,
        selectedBriefIds,
        onBriefIdsChange,
        briefSearch,
        onBriefSearchChange,
        areBriefsLoading,
      }),
      {
        title: "Vendor Billing",
        key: "vendorBilling",
        width: 180,
        render: (_value: unknown, record: IPayoutInfluencer) => (
          <PayoutVendorBillingCell record={record} />
        ),
      },
      {
        title: "Status",
        key: "status",
        width: 180,
        render: (_value: unknown, record: IPayoutInfluencer) => (
          <PayoutStatusCell record={record} />
        ),
      },
      {
        title: "File no.",
        key: "fileNo",
        width: 160,
        ellipsis: true,
        render: (_value: unknown, record: IPayoutInfluencer) => {
          const fileNo = record.bankVerificationExportId;
          if (!fileNo) {
            return <span className={styles.empty}>—</span>;
          }
          return (
            <TextWithTooltip
              text={fileNo}
              limit={16}
              textClassName={styles.fileNo}
            />
          );
        },
      },
      {
        title: "Amount",
        key: "amount",
        width: 110,
        ellipsis: true,
        render: (_value: unknown, record: IPayoutInfluencer) => (
          <span className={styles.amount}>
            {formatPayoutAmount(record.amount)}
          </span>
        ),
      },
      {
        title: "Narration",
        key: "narration",
        width: 220,
        render: (_value: unknown, record: IPayoutInfluencer) => (
          <TextWithTooltip
            text={record.remarks || "—"}
            limit={36}
          />
        ),
      },
      {
        title: "Created",
        key: "createdAt",
        width: 120,
        render: (_value: unknown, record: IPayoutInfluencer) => (
          <span className={styles.date}>
            {formatPayoutDate(record.createdAt)}
          </span>
        ),
      },
      {
        title: "Payment",
        key: "paidAt",
        width: 120,
        render: (_value: unknown, record: IPayoutInfluencer) => (
          <span className={styles.date}>
            {formatPayoutDate(record.externalPaymentDate) || "—"}
          </span>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 160,
        fixed: "right",
        render: (_value: unknown, record: IPayoutInfluencer) => (
          <PayoutActionsCell
            record={record}
            isCancelling={isCancelling}
            onCancelPayout={onCancelPayout}
            onViewPayments={onViewPayments}
            onAddBillingDetails={onAddBillingDetails}
          />
        ),
      },
    ],
    [
      areBriefsLoading,
      briefOptions,
      briefSearch,
      isCancelling,
      onAddBillingDetails,
      onBriefIdsChange,
      onBriefSearchChange,
      onCancelPayout,
      onViewPayments,
      selectedBriefIds,
    ]
  );
}
